import { useCallback, useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import ReactModal from "react-modal";
import { GlobalSearchProvider } from "common/app/command-palette/providers/global-search";
import { getCommonSelectorProps } from "common/app/command-palette/selectors";
import { EntitiesProvider } from "common/app/command-palette/providers/entities";
import { useCommandPaletteShortcuts } from "common/app/command-palette/use-shortcuts";
import { SelectorOption } from "common/widgets/selector/types";
import { groupCommands } from "common/app/command-palette/groups";
import { formatCommand } from "common/app/command-palette/list-item";
import { AccountSettingsProvider } from "common/app/command-palette/providers/account-settings";
import { ContextProvider } from "common/app/command-palette/providers/context";
import { HistoryProvider } from "common/app/command-palette/providers/history";
import { MenuProvider } from "common/app/command-palette/providers/menu";
import { OtherProvider } from "common/app/command-palette/providers/other";
import { ReportsProvider } from "common/app/command-palette/providers/reports";
import { SiteSwitchProvider } from "common/app/command-palette/providers/site-switch";
import {
  Command,
  CommandProvider,
  ModuleRenderer,
  ProviderConfig,
} from "common/app/command-palette/types";
import { Context } from "common/types/context";
import { GoFn } from "common/types/url";
import { Backdrop } from "common/widgets/backdrop";
import { Selector } from "common/widgets/selector";
import { searchCommands } from "./search";

import "./index.scss";

type CommandPaletteProps = {
  context: Context;
  goTo: GoFn;
  onLogout?: () => void;
  providersOverride?: (typeof MenuProvider)[];
};

const defaultProviders = [
  GlobalSearchProvider,
  ContextProvider,
  MenuProvider,
  EntitiesProvider,
  ReportsProvider,
  AccountSettingsProvider,
  SiteSwitchProvider,
  OtherProvider,
  HistoryProvider,
];

export const CommandPalette = ({
  context,
  goTo,
  onLogout,
  providersOverride,
}: CommandPaletteProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [moduleRenderer, setModuleRenderer] = useState<
    ModuleRenderer | undefined
  >(undefined);
  const [commandsVersion, setCommandsVersion] = useState<number>(0);
  const [processedCommands, setProcessedCommands] = useState<
    SelectorOption<Command>[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const providers = useRef<CommandProvider[]>([]);

  const openPalette = useCallback(() => {
    if (!open) {
      unstable_batchedUpdates(() => {
        setOpen(true);
        setModuleRenderer(undefined);
      });
    }
  }, [open]);

  const onClosePalette = useCallback(() => {
    unstable_batchedUpdates(() => {
      setOpen(false);
      setModuleRenderer(undefined);
    });
  }, []);

  const setModuleRendererFn = useCallback((renderer: ModuleRenderer) => {
    setModuleRenderer(() => renderer);
  }, []);

  const handleCommandsChanged = useCallback(() => {
    setCommandsVersion((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const providerConfig: ProviderConfig = {
      setModuleRenderer: setModuleRendererFn,
      onCommandsChanged: handleCommandsChanged,
    };

    const providersToUse = providersOverride ?? defaultProviders;

    if (providers.current.length === 0) {
      const initializedProviders: CommandProvider[] = [];

      providersToUse.forEach((ProviderClass) => {
        try {
          const provider = new ProviderClass(providerConfig);
          initializedProviders.push(provider);
        } catch (e) {
          // Swallow errors during initialization
        }
      });

      providers.current = initializedProviders;
    }

    return () => {
      providers.current.forEach((provider) => {
        try {
          provider.dispose();
        } catch (e) {
          // Swallow errors during disposal
        }
      });
      providers.current = [];
    };
  }, [setModuleRendererFn, handleCommandsChanged, providersOverride]);

  useEffect(() => {
    setIsLoading(true);

    const fetchCommands = async () => {
      try {
        const commandPromises = providers.current.map((provider) => {
          try {
            return provider.getCommands({
              context,
              onLogout,
            });
          } catch (e) {
            return Promise.resolve([]);
          }
        });

        const resolvedCommands = await Promise.all(commandPromises);
        const allCommands = resolvedCommands.flat();
        const grouped = groupCommands(allCommands);

        setProcessedCommands(grouped);
      } catch (err) {
        setProcessedCommands([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommands();
  }, [context, commandsVersion, onLogout]);

  const handleModuleBack = useCallback(() => {
    setModuleRenderer(undefined);
  }, [setModuleRenderer]);

  const handleModalClose = useCallback(() => {
    moduleRenderer ? handleModuleBack() : onClosePalette();
  }, [moduleRenderer, onClosePalette, handleModuleBack]);

  useCommandPaletteShortcuts({
    onToggle: openPalette,
    onClose: open ? handleModalClose : undefined,
  });

  const onCommandSelected = (command: Command | undefined) => {
    if (!command) return;

    const shouldClose = !command.isModule;

    if (command.link) {
      if (shouldClose) onClosePalette();
      goTo(command.link);
    } else if (command.onClick) {
      command.onClick();
      if (shouldClose) {
        onClosePalette();
      }
    } else if (shouldClose) {
      onClosePalette();
    }
  };

  return (
    <ReactModal
      htmlOpenClassName="x-modal-html-open"
      bodyOpenClassName="x-modal-body-open"
      portalClassName="x-modal-portal x-command-palette-portal"
      overlayClassName="x-modal-backdrop"
      className="x-command-palette"
      isOpen={open}
      appElement={document.querySelector(".x-modal-parent") ?? undefined}
      onRequestClose={handleModalClose}
    >
      <Backdrop onClick={onClosePalette} />
      <div className="x-command-wrapper">
        {moduleRenderer ? (
          moduleRenderer({
            context,
            goTo,
            onCommandSelected: onClosePalette,
            onBack: handleModuleBack,
          })
        ) : (
          <Selector<Command>
            placeholder={_("Search for commands...")}
            className="x-command-palette-selector"
            {...getCommonSelectorProps()}
            options={processedCommands}
            getOptionLabel={(option) => option.label}
            formatOption={formatCommand}
            disableSorting={true}
            customSearchFn={searchCommands}
            autoFocus={true}
            isLoading={isLoading}
            value={undefined}
            onChange={onCommandSelected}
          />
        )}
      </div>
    </ReactModal>
  );
};

CommandPalette.displayName = "CommandPalette";
